import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  CircularProgress,
  ClickAwayListener,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import LaunchIcon from "@material-ui/icons/Launch";
import WarningIcon from "@material-ui/icons/Warning";
import { useHistory } from "react-router-dom";
import A_TextField from "../../../../Atoms/Inputs/TextFields/A_TextField";
import A_DatePicker from "../../../../Atoms/Inputs/DateTimePickers/A_DatePicker";
import A_Select from "../../../../Atoms/Inputs/Selects/A_Select";
import A_Button from "../../../../Atoms/Inputs/Buttons/A_Button";
import A_SimpleDialog from "../../../../Atoms/Feedback/Dialogs/A_SimpleDialog";
import LookupSearchSelect from "./LookupSearchSelect";
import * as toastActions from "../../../../../../store/actions/toastActions";
import * as currentPageActions from "../../../../../../store/actions/currentPageActions";
import { toastSeverity } from "../../../../../../Utils/config/toastConfig";
import useStyles from "./Add_Loan.styles";
import {
  ALL_FIELDS,
  FEE_GROUPS,
  FEE_MODE_DEFAULTS,
  FEE_MODE_OPTIONS,
  LOAN_STEPS,
  PERCENTAGE_FIELD_KEYS,
} from "./Add_Loan.constants";
import {
  buildDeveloperLoanSampleData,
  buildFormStateFromSource,
  buildInitialState,
  clearLoanDraft,
  getFieldGroups,
  getFieldHelperText,
  getFieldOptions,
  getLoanDraftStorageKey,
  getRecordSource,
  hasDraftableLoanData,
  hasFieldValue,
  isFieldRequired,
  isNumericType,
  loadBorrowerLookupOptions,
  loadOptionSets,
  loadLoanDraft,
  sanitizeLoanDraft,
  saveLoanDraft,
  shouldShowField,
} from "./Add_Loan.utils";
import { httpPost, httpPut } from "../../../../../../Utils/apis/apis";
import EgSearchField from "./EgSearchField";
import { useSelector } from "react-redux";
import { getCRMContactInfo } from "../../../../../../Utils/Helpers/common";
import { message } from "../../../../../../Utils/config/messages";
import { useParams } from "react-router";

const addLoanPropTypes = {
  formConfig: PropTypes.shape({
    data: PropTypes.shape({
      BorrowerId: PropTypes.any,
      borrowerId: PropTypes.any,
      _partitionKey: PropTypes.any,
    }),
  }),
  isEdit: PropTypes.bool,
  setToast: PropTypes.func,
  onSaveSuccess: PropTypes.func,
  onClose: PropTypes.func,
  getCurrentPage: PropTypes.func,
};

const addLoanDefaultProps = {
  formConfig: null,
  isEdit: false,
  isEditForm: false,
  setToast: () => {},
  onSaveSuccess: null,
  onClose: null,
  getCurrentPage: null,
};

const CURRENCY_SYMBOLS = {
  GBP: "£",
  USD: "$",
  EUR: "€",
};

const AddLoanComponent = ({
  formConfig,
  isEdit,
  setToast,
  onSaveSuccess,
  onClose,
  getCurrentPage,
}) => {
  const classes = useStyles();
  const { pageUrl, crmId } = useParams();
  const formCardRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(() => buildInitialState());
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOptionsLoading, setIsOptionsLoading] = useState(true);
  const [optionSetData, setOptionSetData] = useState({});
  const [borrowerOptions, setBorrowerOptions] = useState([]);
  const [borrowerSelection, setBorrowerSelection] = useState(null);
  const [feeModes, setFeeModes] = useState(FEE_MODE_DEFAULTS);
  const [fieldRenderKey, setFieldRenderKey] = useState(0);
  const [availableDraft, setAvailableDraft] = useState(null);
  const [isDraftBootstrapComplete, setIsDraftBootstrapComplete] =
    useState(false);
  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [searchResults, setSearchResults] = useState({});
  const [isFieldSearching, setIsFieldSearching] = useState({});
  const [isResultsVisible, setIsResultsVisible] = useState({});
  const [showFinishConfirmation, setShowFinishConfirmation] = useState(false);
  const history = useHistory();
  const effectiveEditMode = isEdit;
  const canUseDrafts = !effectiveEditMode;
  const currentStep = LOAN_STEPS[activeStep];
  const isLastStep = activeStep === LOAN_STEPS.length - 1;
  let clientName =
    window?.location?.hostname?.split(".")?.length > 0
      ? window.location.hostname.split(".")[0]
      : "";
  const isDeveloperAutofillEnabled = clientName === "localhost" ? true : false;
  let { crmContactUserEmail } = getCRMContactInfo();
  const LoggedInUserEmail = useSelector((state) =>
    state.getLoggedInUserReducer?.data?.[0]?.properties?.email
      ? state.getLoggedInUserReducer?.data?.[0]?.properties?.email
      : crmContactUserEmail
  );
  const draftOwnerIdentifier =
    LoggedInUserEmail || crmContactUserEmail || "anonymous";
  const draftStorageKey = canUseDrafts
    ? getLoanDraftStorageKey(draftOwnerIdentifier)
    : null;

  const getVisibleFieldsForStep = useCallback(
    (step, data = formData, modes = feeModes) =>
      step.fields.filter((field) =>
        shouldShowField(field, data, modes, effectiveEditMode)
      ),
    [formData, feeModes, effectiveEditMode]
  );

  const getFieldByKey = useCallback(
    (backendKey) => ALL_FIELDS.find((field) => field.backendKey === backendKey),
    []
  );

  useEffect(() => {
    let isMounted = true;

    const initializeOptionSets = async () => {
      setIsOptionsLoading(true);
      const [optionSetObject, borrowerLookupOptions] = await Promise.all([
        loadOptionSets(),
        loadBorrowerLookupOptions(),
      ]);
      if (!isMounted) {
        return;
      }
      setOptionSetData(optionSetObject);
      setBorrowerOptions(borrowerLookupOptions);
      setIsOptionsLoading(false);
      setFieldRenderKey((prevState) => prevState + 1);
    };

    initializeOptionSets();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!effectiveEditMode) {
      setFormData(buildInitialState());
      setBorrowerSelection(null);
      setActiveStep(0);
      setFeeModes(FEE_MODE_DEFAULTS);
      return;
    }

    const source = getRecordSource(formConfig);
    const initialData = buildFormStateFromSource(source);
    setFormData(initialData);
    setBorrowerSelection(
      source?.BorrowerName
        ? {
            label: source.BorrowerName,
            value: source.BorrowerName,
            borrowerId:
              source?.BorrowerId ||
              source?.borrowerId ||
              source?.BorrowerID ||
              "",
          }
        : null
    );

    setFeeModes((prevState) => {
      const nextState = { ...prevState };

      Object.keys(FEE_MODE_DEFAULTS).forEach((group) => {
        const groupFields = ALL_FIELDS.filter(
          (field) => field.feeModeGroup === group
        );
        const amountField = groupFields.find(
          (field) => field.feeModeValue === "amount"
        );
        const rateField = groupFields.find(
          (field) => field.feeModeValue === "rate"
        );

        if (amountField && hasFieldValue(initialData[amountField.backendKey])) {
          nextState[group] = "amount";
        } else if (
          rateField &&
          hasFieldValue(initialData[rateField.backendKey])
        ) {
          nextState[group] = "rate";
        }
      });

      return nextState;
    });
  }, [effectiveEditMode, formConfig]);

  useEffect(() => {
    let isMounted = true;

    const fetchDraft = async () => {
      if (!canUseDrafts || !draftStorageKey) {
        if (isMounted) {
          setAvailableDraft(null);
          setIsDraftBootstrapComplete(true);
        }
        return;
      }

      const loadedDraft = await loadLoanDraft(draftStorageKey);
      const draft = sanitizeLoanDraft(loadedDraft);

      if (!isMounted) {
        return;
      }

      setAvailableDraft(
        draft &&
          hasDraftableLoanData(draft.formData, draft.feeModes, draft.activeStep)
          ? draft
          : null
      );
      setIsDraftBootstrapComplete(true);
    };

    fetchDraft();

    return () => {
      isMounted = false;
    };
  }, [canUseDrafts, draftStorageKey]);

  useEffect(() => {
    setFormErrors({});
  }, [activeStep]);

  useEffect(() => {
    setFeeModes((prevState) => {
      const nextState = { ...prevState };

      Object.keys(FEE_MODE_DEFAULTS).forEach((group) => {
        const groupFields = ALL_FIELDS.filter(
          (field) => field.feeModeGroup === group
        );
        const amountField = groupFields.find(
          (field) => field.feeModeValue === "amount"
        );
        const rateField = groupFields.find(
          (field) => field.feeModeValue === "rate"
        );

        if (amountField && hasFieldValue(formData[amountField.backendKey])) {
          nextState[group] = "amount";
        } else if (rateField && hasFieldValue(formData[rateField.backendKey])) {
          nextState[group] = "rate";
        }
      });

      return nextState;
    });
  }, [formData]);

  useEffect(() => {
    const hiddenFieldKeys = ALL_FIELDS.filter(
      (field) => !shouldShowField(field, formData, feeModes, effectiveEditMode)
    ).map((field) => field.backendKey);

    if (hiddenFieldKeys.length === 0) {
      return;
    }

    const hasHiddenValues = hiddenFieldKeys.some((key) =>
      hasFieldValue(formData[key])
    );

    if (!hasHiddenValues) {
      return;
    }

    setFormData((prevState) => {
      const nextState = { ...prevState };

      hiddenFieldKeys.forEach((key) => {
        const field = getFieldByKey(key);

        if (!field || !hasFieldValue(nextState[key])) {
          return;
        }

        nextState[key] = field.type === "file" ? [] : "";
      });

      return nextState;
    });

    setFormErrors((prevState) => {
      const nextErrors = { ...prevState };
      hiddenFieldKeys.forEach((key) => delete nextErrors[key]);
      return nextErrors;
    });
  }, [feeModes, formData]);

  useEffect(() => {
    if (formCardRef.current) {
      formCardRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [activeStep]);

  const focusField = useCallback((backendKey) => {
    window.setTimeout(() => {
      const element =
        document.getElementById(backendKey) ||
        document.querySelector(`[name="${backendKey}"]`) ||
        document.querySelector(`[id^="${backendKey}"] input`) ||
        document.querySelector(`#formcontrol${backendKey} input`) ||
        document.querySelector(`#formcontrol${backendKey} [role="button"]`);

      if (element?.scrollIntoView) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      if (element?.focus) {
        element.focus();
      }
    }, 50);
  }, []);

  const validateField = useCallback(
    (field, value, data = formData, modes = feeModes) => {
      const trimmedValue = typeof value === "string" ? value.trim() : value;
      const isEmpty = !hasFieldValue(trimmedValue);

      if (isFieldRequired(field, data, modes, effectiveEditMode) && isEmpty) {
        return message.FIELD_REQUIRED;
      }

      if (isEmpty) {
        return "";
      }

      if (
        (field.type === "int" || field.type === "integer") &&
        !/^-?\d+$/.test(`${trimmedValue}`)
      ) {
        return message.ENTER_WHOLE_NUMBER;
      }

      if (isNumericType(field.type)) {
        const numericValue = Number(trimmedValue);

        if (Number.isNaN(numericValue)) {
          return message.ENTER_VALID_NUMBER;
        }

        if (PERCENTAGE_FIELD_KEYS.includes(field.backendKey)) {
          if (numericValue < 0 || numericValue > 100) {
            return message.ENTER_PERCENTAGE_RANGE;
          }
        }

        if (field.validationRule?.includes(">= 1") && numericValue < 1) {
          return message.VALUE_MIN_ONE;
        }
      }

      if (
        field.backendKey === "Currency" &&
        !/^[A-Z]{3}$/.test(`${trimmedValue}`)
      ) {
        return message.CURRENCY_CODE_FORMAT;
      }

      return "";
    },
    [formData, feeModes]
  );

  const validateStep = useCallback(
    (step) => {
      const visibleFields = getVisibleFieldsForStep(step);
      const stepErrors = {};

      visibleFields.forEach((field) => {
        const error = validateField(
          field,
          formData[field.backendKey],
          formData,
          feeModes
        );

        if (error) {
          stepErrors[field.backendKey] = error;
        }
      });

      setFormErrors((prevState) => {
        const nextErrors = { ...prevState };
        visibleFields.forEach((field) => delete nextErrors[field.backendKey]);
        return { ...nextErrors, ...stepErrors };
      });

      return {
        isValid: Object.keys(stepErrors).length === 0,
        firstInvalidField: visibleFields.find(
          (field) => stepErrors[field.backendKey]
        )?.backendKey,
      };
    },
    [getVisibleFieldsForStep, validateField, formData, feeModes]
  );

  const dismissAvailableDraft = useCallback(() => {
    if (availableDraft) {
      setAvailableDraft(null);
    }
  }, [availableDraft]);

  const updateFieldValue = useCallback(
    (id, value) => {
      const field = getFieldByKey(id);
      const nextData = {
        ...formData,
        [id]: value,
      };

      dismissAvailableDraft();
      setFormData(nextData);

      if (!field) {
        return;
      }

      setFormErrors((prevState) => ({
        ...prevState,
        [id]: validateField(field, value, nextData, feeModes),
      }));
    },
    [dismissAvailableDraft, formData, getFieldByKey, validateField, feeModes]
  );

  const handleTextChange = (event) => {
    const { id, value } = event.target;
    updateFieldValue(id, value);
  };

  const handleDateChange = (value, id) => {
    updateFieldValue(id, value === 0 ? "" : value);
  };

  const handleSelectChange = (selectedOption, id) => {
    updateFieldValue(id, selectedOption?.value || "");
  };

  const handleBooleanChange = (event) => {
    const { name, value } = event.target;
    updateFieldValue(name, value);
  };

  const handleLookupChange = (selected, id) => {
    if (Array.isArray(selected)) {
      updateFieldValue(
        id,
        selected.map((o) => o?.value).filter((v) => v !== undefined)
      );
      return;
    }

    if (id === "BorrowerName") {
      setBorrowerSelection(
        selected
          ? {
              label: selected.label || selected.value || "",
              value: selected.value || selected.label || "",
              borrowerId: selected.borrowerId || "",
            }
          : null
      );
      updateFieldValue(id, selected?.label || selected?.value || "");
      return;
    }

    updateFieldValue(id, selected?.value || "");
  };

  const handleFieldSearch = async (fieldId, value, isExplicit = false) => {
    const trimmedValue = value?.trim() || "";
    if (isFieldSearching[fieldId]) return;
    if (!trimmedValue || trimmedValue.length < 2) {
      setSearchResults((prev) => ({ ...prev, [fieldId]: [] }));
      return;
    }

    try {
      setIsFieldSearching((prev) => ({ ...prev, [fieldId]: true }));
      const payload = [
        {
          id: "Search_Results",
          spName: "DF.usp_PrefilledLoanDetailsByPostcode",
          parameters: {
            postCode: value.trim(),
          },
        },
      ];
      const response = await httpPost(
        "/SQLQuery/getDataFromMultipleSPs?chartSPs=false",
        payload
      );
      const data = response?.Search_Results || [];

      setSearchResults((prev) => ({ ...prev, [fieldId]: data }));

      if (data.length === 0 && isExplicit) {
        setToast({
          type: toastSeverity.INFO,
          severity: toastSeverity.INFO,
          message: message.NO_POSTCODE_RESULTS,
          status: true,
          duration: 3000,
        });
      }
    } catch (error) {
      setToast({
        type: toastSeverity.ERROR,
        severity: toastSeverity.ERROR,
        message: message.SEARCH_FAILED,
        status: true,
        duration: 3000,
      });
    } finally {
      setIsFieldSearching((prev) => ({ ...prev, [fieldId]: false }));
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const currentStepFields = LOAN_STEPS[activeStep]?.fields || [];
      currentStepFields.forEach((field) => {
        if (field.type === "search-string" && formData[field.backendKey]) {
          handleFieldSearch(field.backendKey, formData[field.backendKey]);
        }
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [activeStep, formData.Postcode]);

  const toggleResultsVisibility = (fieldId, visible) => {
    setIsResultsVisible((prev) => ({ ...prev, [fieldId]: visible }));
  };

  const handleFeeModeChange = (group, value) => {
    dismissAvailableDraft();

    if (!isEdit) {
      const groupFields = ALL_FIELDS.filter(
        (field) => field.feeModeGroup === group
      );
      groupFields.forEach((field) => {
        if (field.feeModeValue && field.feeModeValue !== value) {
          updateFieldValue(field.backendKey, "");
        }
      });
    }

    setFeeModes((prevState) => ({
      ...prevState,
      [group]: value,
    }));
  };

  const handleNext = useCallback(() => {
    const validationResult = validateStep(currentStep);

    if (!validationResult.isValid) {
      if (validationResult.firstInvalidField) {
        focusField(validationResult.firstInvalidField);
      }
      return;
    }

    setActiveStep((prevStep) => Math.min(prevStep + 1, LOAN_STEPS.length - 1));
  }, [validateStep, currentStep, focusField]);

  const handleBack = () => {
    dismissAvailableDraft();
    setActiveStep((prevStep) => Math.max(prevStep - 1, 0));
  };

  const handleRestoreDraft = useCallback(() => {
    if (!availableDraft) {
      return;
    }

    setFormData(availableDraft.formData);
    setFeeModes({
      ...FEE_MODE_DEFAULTS,
      ...availableDraft.feeModes,
    });
    setBorrowerSelection(availableDraft.borrowerSelection || null);
    setActiveStep(
      Math.min(
        Math.max(availableDraft.activeStep || 0, 0),
        LOAN_STEPS.length - 1
      )
    );
    setFieldRenderKey((prevState) => prevState + 1);

    // Trigger search if postcode exists in draft
    const postcode = availableDraft.formData?.Postcode;
    if (postcode && postcode.trim().length >= 2) {
      handleFieldSearch("Postcode", postcode);
    }

    setAvailableDraft(null);
    setToast({
      type: toastSeverity.SUCCESS,
      severity: toastSeverity.SUCCESS,
      message: message.LOAN_DRAFT_RESTORED,
      status: true,
      duration: 3000,
    });
  }, [availableDraft, setToast]);

  const handleDiscardDraft = useCallback(async () => {
    if (draftStorageKey) {
      await clearLoanDraft(draftStorageKey);
    }

    setAvailableDraft(null);
    setFormData(buildInitialState());
    setBorrowerSelection(null);
    setFeeModes(FEE_MODE_DEFAULTS);
    setActiveStep(0);
    setFormErrors({});
    setSearchResults({});
    setIsFieldSearching({});
    setIsResultsVisible({});
    setFieldRenderKey((prevState) => prevState + 1);
    setToast({
      type: toastSeverity.SUCCESS,
      severity: toastSeverity.SUCCESS,
      message: message.LOAN_DRAFT_DISCARDED,
      status: true,
      duration: 3000,
    });
  }, [draftStorageKey, setToast]);

  const saveDraft = useCallback(
    async ({
      showEmptyWarning = true,
      successMessage = message.LOAN_DRAFT_SAVED,
    } = {}) => {
      if (
        !canUseDrafts ||
        !draftStorageKey ||
        !isDraftBootstrapComplete ||
        isDraftSaving
      ) {
        return { saved: false, skipped: true };
      }

      if (!hasDraftableLoanData(formData, feeModes, activeStep)) {
        if (showEmptyWarning) {
          setToast({
            type: toastSeverity.ERROR,
            severity: toastSeverity.ERROR,
            message: message.LOAN_DRAFT_EMPTY_WARNING,
            status: true,
            duration: 3000,
          });
        }
        return { saved: false, skipped: true, empty: true };
      }

      setIsDraftSaving(true);

      const saved = await saveLoanDraft(draftStorageKey, {
        version: 1,
        activeStep,
        feeModes,
        formData,
        borrowerSelection,
        updatedAt: new Date().toISOString(),
      });

      setIsDraftSaving(false);

      setToast({
        type: saved ? toastSeverity.SUCCESS : toastSeverity.ERROR,
        severity: saved ? toastSeverity.SUCCESS : toastSeverity.ERROR,
        message: saved ? successMessage : message.SOMETHING_WENT_WRONG,
        status: true,
        duration: saved ? 3000 : 5000,
      });

      if (saved) {
        setAvailableDraft(null);
      }

      return { saved, skipped: false };
    },
    [
      canUseDrafts,
      draftStorageKey,
      isDraftBootstrapComplete,
      isDraftSaving,
      formData,
      feeModes,
      activeStep,
      borrowerSelection,
      setToast,
    ]
  );

  const handleSaveDraft = useCallback(() => saveDraft(), [saveDraft]);

  const handleDeveloperRandomFill = useCallback(() => {
    const sampleData = buildDeveloperLoanSampleData(
      formData,
      optionSetData,
      feeModes,
      borrowerOptions
    );
    const autofillBorrower = borrowerOptions[0] || null;

    dismissAvailableDraft();
    setFormData(sampleData.formData);
    setBorrowerSelection(
      autofillBorrower
        ? {
            label: autofillBorrower.label || autofillBorrower.value || "",
            value: autofillBorrower.value || autofillBorrower.label || "",
            borrowerId: autofillBorrower.borrowerId || "",
          }
        : null
    );
    setFeeModes(sampleData.feeModes);
    setFormErrors({});
    setFieldRenderKey((prevState) => prevState + 1);
  }, [
    borrowerOptions,
    dismissAvailableDraft,
    feeModes,
    formData,
    optionSetData,
  ]);

  const handleFinish = () => {
    const validationResult = validateStep(currentStep);

    if (!validationResult.isValid) {
      if (validationResult.firstInvalidField) {
        focusField(validationResult.firstInvalidField);
      }
      return;
    }

    setShowFinishConfirmation(true);
  };

  const executeSubmission = useCallback(async () => {
    setShowFinishConfirmation(false);

    const properties = ALL_FIELDS.reduce((result, field) => {
      let value = formData[field.backendKey];
      const isVisible = shouldShowField(
        field,
        formData,
        feeModes,
        effectiveEditMode
      );

      if (!isVisible) {
        result[field.backendKey] = field.type === "file" ? [] : "";
        return result;
      }

      if (value === null || value === undefined) {
        value = field.type === "file" ? [] : "";
      }

      if (field.type === "date" || field.type === "datetime") {
        result[field.backendKey] = value;
        return result;
      }

      if (field.type === "optionset") {
        result[field.backendKey] = value || "";
      } else {
        result[field.backendKey] = value;
      }

      return result;
    }, {});
    properties.BorrowerName = formData.BorrowerName || "";
    properties.BorrowerId =
      borrowerSelection?.borrowerId ||
      formConfig?.data?.BorrowerId ||
      formConfig?.data?.borrowerId ||
      "";

    setIsSubmitting(true);
    properties.CreatedBy = LoggedInUserEmail;
    properties.statusPipeline = "pe-deals-workflow-config";
    properties.currentStatus = "Enquiry";

    const insertPayload = {
      id: effectiveEditMode
        ? formConfig?.data?._partitionKey ||
          formData.ApplicationId ||
          crmId ||
          null
        : null,
      label: "loan",
      type: "vertex",
      properties,
      edges: [],
      security: {
        read: { groupids: [], users: [] },
        update: { groupids: [], users: [] },
        full: { groupids: [], users: [] },
        privateRecord: true,
      },
      duplicateDetection: null,
      submitToStoredProc: effectiveEditMode
        ? "DF.usp_EditLoan"
        : "DF.usp_AddLoan",
    };

    try {
      const response = effectiveEditMode
        ? await httpPut(
            "/HTBCRMData/update?validationType=loan",
            insertPayload,
            {},
            true
          )
        : await httpPost(
            "/HTBCRMData/insert?validationType=loan",
            insertPayload,
            {},
            true
          );
      if (response?.data?.id != null) {
        if (canUseDrafts && draftStorageKey) {
          await clearLoanDraft(draftStorageKey);
        }
        setToast({
          type: toastSeverity.SUCCESS,
          severity: toastSeverity.SUCCESS,
          message: effectiveEditMode ? message.LOAN_UPDATED : message.LOAN_SAVED,
          status: true,
          duration: 4000,
        });
        if (effectiveEditMode) {
          if (pageUrl && crmId && getCurrentPage) {
            getCurrentPage({ pageUrl, crmId });
          }
          if (onClose) {
            onClose();
          }
        } else if (onSaveSuccess) {
          onSaveSuccess(response.data.id);
        }
        return;
      }

      const errorMessage =
        response?.message ||
        response?.response?.data?.message ||
        message.SOMETHING_WENT_WRONG;

      setToast({
        type: toastSeverity.ERROR,
        severity: toastSeverity.ERROR,
        message: errorMessage,
        status: true,
        duration: 5000,
      });
    } catch (error) {
      setToast({
        type: toastSeverity.ERROR,
        severity: toastSeverity.ERROR,
        message: message.SOMETHING_WENT_WRONG,
        status: true,
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    validateStep,
    currentStep,
    focusField,
    formData,
    borrowerSelection,
    formConfig,
    effectiveEditMode,
    canUseDrafts,
    draftStorageKey,
    setToast,
    onSaveSuccess,
    onClose,
    getCurrentPage,
    pageUrl,
    crmId,
    LoggedInUserEmail,
    feeModes,
  ]);

  const renderField = (field) => {
    if (field.type === "lookup") {
      const options =
        field.backendKey === "BorrowerName"
          ? borrowerOptions
          : getFieldOptions(field, formData[field.backendKey], optionSetData) ||
            [];

      return (
        <LookupSearchSelect
          key={`${field.backendKey}-${fieldRenderKey}-${options.length}`}
          id={field.backendKey}
          label={field.label}
          fullWidth
          required={isFieldRequired(
            field,
            formData,
            feeModes,
            effectiveEditMode
          )}
          defaultValue={formData[field.backendKey]}
          options={options}
          multiple={!!field.multiple}
          onChange={handleLookupChange}
          fieldHelperText={getFieldHelperText(field)}
          error={!!formErrors[field.backendKey]}
          helperText={formErrors[field.backendKey]}
        />
      );
    }

    if (field.type === "optionset") {
      const options = getFieldOptions(
        field,
        formData[field.backendKey],
        optionSetData
      );

      return (
        <A_Select
          key={`${field.backendKey}-${fieldRenderKey}-${options.length}`}
          id={field.backendKey}
          label={field.label}
          fullWidth
          variant="standard"
          required={isFieldRequired(
            field,
            formData,
            feeModes,
            effectiveEditMode
          )}
          defaultValue={formData[field.backendKey]}
          options={options}
          onChange={handleSelectChange}
          fieldHelperText={getFieldHelperText(field)}
          error={!!formErrors[field.backendKey]}
          helperText={formErrors[field.backendKey]}
        />
      );
    }
    if (field.type === "boolean") {
      const helperText =
        formErrors[field.backendKey] || getFieldHelperText(field);

      return (
        <FormControl
          key={`${field.backendKey}-${fieldRenderKey}`}
          component="fieldset"
          fullWidth
          error={!!formErrors[field.backendKey]}
          className={classes.booleanFieldWrap}
        >
          <Typography className={classes.booleanFieldLabel}>
            {field.label}
          </Typography>
          <RadioGroup
            row
            name={field.backendKey}
            value={formData[field.backendKey]}
            onChange={handleBooleanChange}
            className={classes.radioGroup}
          >
            {(field.fixedOptions || []).map((option) => (
              <FormControlLabel
                key={`${field.backendKey}-${String(option.value)}`}
                value={String(option.value)}
                control={<Radio color="primary" size="small" />}
                label={option.label}
              />
            ))}
          </RadioGroup>
          {helperText ? (
            <Typography
              className={classes.booleanFieldHelper}
              style={
                formErrors[field.backendKey] ? { color: "#f44336" } : undefined
              }
            >
              {helperText}
            </Typography>
          ) : null}
        </FormControl>
      );
    }
    if (field.type === "search-string") {
      let fieldResults = searchResults[field.backendKey] || [];
      const isSelfReferentialOnly =
        effectiveEditMode &&
        fieldResults.length === 1 &&
        String(fieldResults[0]["Loan Id"]) === String(crmId);

      if (effectiveEditMode) {
        fieldResults = fieldResults.filter(
          (res) => String(res["Loan Id"]) !== String(crmId)
        );
      }

      return (
        <div className={classes.searchResultContainer}>
          <A_TextField
            key={`${field.backendKey}-${fieldRenderKey}`}
            id={field.backendKey}
            name={field.backendKey}
            label={field.label}
            variant="standard"
            fullWidth
            defaultValue={formData[field.backendKey] || ""}
            inputType="value"
            onChange={handleTextChange}
            onBlur={() => {
              setTimeout(() => {
                toggleResultsVisibility(field.backendKey, false);
              }, 200);
            }}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleFieldSearch(
                  field.backendKey,
                  formData[field.backendKey],
                  true
                );
                toggleResultsVisibility(field.backendKey, true);
              }
            }}
            required={isFieldRequired(
              field,
              formData,
              feeModes,
              effectiveEditMode
            )}
            error={!!formErrors[field.backendKey]}
            helperText={formErrors[field.backendKey]}
            InputProps={{
              endAdornment: (
                <>
                  {isFieldSearching[field.backendKey] ? (
                    <CircularProgress
                      size={20}
                      className={classes.searchingSpinner}
                    />
                  ) : formData[field.backendKey]?.trim()?.length >= 2 ? (
                    <IconButton
                      size="small"
                      className={classes.searchIconButton}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFieldSearch(
                          field.backendKey,
                          formData[field.backendKey],
                          true
                        );
                        toggleResultsVisibility(field.backendKey, true);
                      }}
                    >
                      <SearchIcon />
                    </IconButton>
                  ) : null}
                </>
              ),
            }}
          />
          {isResultsVisible[field.backendKey] && !isSelfReferentialOnly && (
            <ClickAwayListener
              onClickAway={() =>
                toggleResultsVisibility(field.backendKey, false)
              }
            >
              <Paper className={classes.searchResultPaper} elevation={3}>
                {isFieldSearching[field.backendKey] &&
                fieldResults.length === 0 ? (
                  <div className={classes.searchResultLoading}>
                    <CircularProgress size={16} />
                    <Typography variant="caption">Searching...</Typography>
                  </div>
                ) : fieldResults.length > 0 ? (
                  <>
                    <Typography className={classes.searchResultHeader}>
                      Existing Loans for this Postcode
                    </Typography>
                    {fieldResults.map((res, index) => (
                      <div
                        key={index}
                        className={classes.searchResultItem}
                        onClick={() =>
                          history.push(
                            `/loan/${res["Loan Id"]}/ac4f4d6d-6477-4cb8-ba02-c27b065d08fc`
                          )
                        }
                      >
                        <div className={classes.searchResultInfo}>
                          <Typography className={classes.searchResultTitle}>
                            {res["Loan Name"] || "Unnamed Loan"}
                          </Typography>
                          <Typography className={classes.searchResultSubTitle}>
                            {res["Borrower Name"] || "Unknown Entity"} •{" "}
                            {res["Loan Status"] || "No status available"}
                          </Typography>
                        </div>
                        <div className={classes.searchResultAction}>
                          <LaunchIcon style={{ fontSize: 16 }} />
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <div className={classes.searchResultEmpty}>
                    <Typography variant="caption">
                      No applications found.
                    </Typography>
                  </div>
                )}
              </Paper>
            </ClickAwayListener>
          )}
        </div>
      );
    }

    if (field.type === "exposure-group-search") {
      return (
        <EgSearchField
          key={`${field.backendKey}-${fieldRenderKey}`}
          id={field.backendKey}
          label={field.label}
          value={formData[field.backendKey] || ""}
          onChange={(val) => updateFieldValue(field.backendKey, val)}
          required={isFieldRequired(field, formData, feeModes, effectiveEditMode)}
          error={!!formErrors[field.backendKey]}
          helperText={formErrors[field.backendKey]}
          fetchUrl="/ExposureGroup/getall"
          createUrl="/exposuregroup/addgroup"
          createPayloadKey="name"
          displayKey="name"
        />
      );
    }

    if (field.type === "date" || field.type === "datetime") {
      return (
        <A_DatePicker
          key={`${field.backendKey}-${fieldRenderKey}`}
          id={field.backendKey}
          label={field.label}
          fullWidth
          margin="dense"
          inputVariant="standard"
          required={isFieldRequired(
            field,
            formData,
            feeModes,
            effectiveEditMode
          )}
          defaultValue={formData[field.backendKey] || null}
          onChange={handleDateChange}
          fieldHelperText={getFieldHelperText(field)}
          error={!!formErrors[field.backendKey]}
          helperText={formErrors[field.backendKey]}
        />
      );
    }

    return (
      <A_TextField
        key={`${field.backendKey}-${fieldRenderKey}`}
        id={field.backendKey}
        name={field.backendKey}
        label={field.label}
        variant="standard"
        type={isNumericType(field.type) ? "number" : undefined}
        fullWidth
        required={isFieldRequired(field, formData, feeModes, effectiveEditMode)}
        defaultValue={formData[field.backendKey]}
        inputType="value"
        multiline={field.multiline}
        rows={field.rows}
        onChange={handleTextChange}
        adornment={
          PERCENTAGE_FIELD_KEYS.includes(field.backendKey) ||
          field.backendKey.endsWith("Rate")
            ? { position: "end", value: "%" }
            : (field.type === "float" ||
                field.type === "decimal" ||
                field.helperText === "GBP") &&
              !field.multiline
            ? {
                position: "start",
                value:
                  CURRENCY_SYMBOLS[formData.Currency] ||
                  formData.Currency ||
                  "£",
              }
            : undefined
        }
        fieldHelperText={getFieldHelperText(field)}
        error={!!formErrors[field.backendKey]}
        helperText={formErrors[field.backendKey]}
      />
    );
  };

  const renderFieldGrid = (field) => (
    <Grid
      item
      xs={12}
      sm={field.fullWidth || field.multiline ? 12 : 6}
      key={field.backendKey}
    >
      {renderField(field)}
    </Grid>
  );

  const renderStandardStep = () => {
    const visibleFields = getVisibleFieldsForStep(currentStep);
    const groups = getFieldGroups(visibleFields);
    const renderedFeeGroups = new Set();

    return groups.map((group) => (
      <div key={group.title || "default"} className={classes.subSectionWrap}>
        {group.title ? (
          <Typography className={classes.subSectionTitle}>
            {group.title}
          </Typography>
        ) : null}
        <Grid container spacing={2}>
          {group.items.map((field) => {
            if (field.feeModeGroup && field.feeModeValue) {
              if (renderedFeeGroups.has(field.feeModeGroup)) {
                return null;
              }
              renderedFeeGroups.add(field.feeModeGroup);

              const rateField = group.items.find(
                (f) =>
                  f.feeModeGroup === field.feeModeGroup &&
                  f.feeModeValue === "rate"
              );
              const amountField = group.items.find(
                (f) =>
                  f.feeModeGroup === field.feeModeGroup &&
                  f.feeModeValue === "amount"
              );

              if (rateField && amountField) {
                const activeField =
                  feeModes[field.feeModeGroup] === "amount"
                    ? amountField
                    : rateField;

                return (
                  <Grid
                    item
                    xs={12}
                    sm={activeField.fullWidth || activeField.multiline ? 12 : 6}
                    key={field.feeModeGroup}
                  >
                    <div
                      className={classes.feeModeWrap}
                      style={{ marginBottom: 4 }}
                    >
                      <Typography className={classes.feeModeLabel}>
                        Enter as
                      </Typography>
                      <FormControl component="fieldset">
                        <RadioGroup
                          className={classes.radioGroup}
                          value={feeModes[field.feeModeGroup]}
                          onChange={(event) =>
                            handleFeeModeChange(
                              field.feeModeGroup,
                              event.target.value
                            )
                          }
                        >
                          {FEE_MODE_OPTIONS.map((option) => (
                            <FormControlLabel
                              key={option.value}
                              value={option.value}
                              control={<Radio color="primary" size="small" />}
                              label={option.label}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </div>
                    {renderField(activeField)}
                  </Grid>
                );
              }
            }
            return renderFieldGrid(field);
          })}
        </Grid>
      </div>
    ));
  };

  const renderFeesStep = () => {
    const nonFeeFields = currentStep.fields.filter(
      (field) => !field.feeModeGroup
    );

    return (
      <Grid container spacing={2}>
        {FEE_GROUPS.map((group) => {
          const rateField = currentStep.fields.find(
            (field) =>
              field.feeModeGroup === group.key && field.feeModeValue === "rate"
          );
          const amountField = currentStep.fields.find(
            (field) =>
              field.feeModeGroup === group.key &&
              field.feeModeValue === "amount"
          );
          const relatedFields = currentStep.fields.filter(
            (field) => field.feeModeGroup === group.key && !field.feeModeValue
          );
          const activeValueField =
            (feeModes[group.key] === "amount" ? amountField : rateField) ||
            amountField ||
            rateField;

          if (!rateField && !amountField && relatedFields.length === 0) {
            return null;
          }

          return (
            <Grid item xs={12} sm={6} key={group.key}>
              <div className={classes.feeCard}>
                <Typography className={classes.feeCardTitle}>
                  {group.title}
                </Typography>
                {rateField && amountField && (
                  <div className={classes.feeModeWrap}>
                    <Typography className={classes.feeModeLabel}>
                      Enter as
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        className={classes.radioGroup}
                        value={feeModes[group.key]}
                        onChange={(event) =>
                          handleFeeModeChange(group.key, event.target.value)
                        }
                      >
                        {FEE_MODE_OPTIONS.map((option) => (
                          <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={<Radio color="primary" size="small" />}
                            label={option.label}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </div>
                )}

                {activeValueField ? (
                  <div className={classes.feeFieldWrap}>
                    {renderField(activeValueField)}
                  </div>
                ) : null}

                {relatedFields.map((field) => (
                  <div className={classes.feeFieldWrap} key={field.backendKey}>
                    {renderField(field)}
                  </div>
                ))}
              </div>
            </Grid>
          );
        })}

        {nonFeeFields.map((field) => renderFieldGrid(field))}
      </Grid>
    );
  };

  return (
    <div className={classes.root}>
      <div className={classes.headerCard}>
        <div className={classes.stepperWrap}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {LOAN_STEPS.map((step) => (
              <Step key={step.title}>
                <StepLabel
                  classes={{
                    label: classes.stepLabel,
                    active: classes.stepLabelActive,
                    completed: classes.stepLabelCompleted,
                  }}
                >
                  {step.title}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </div>
      </div>

      <Paper elevation={0} className={classes.formCard}>
        <div className={classes.fieldsContainer} ref={formCardRef}>
          {availableDraft ? (
            <div className={classes.draftBanner}>
              <div className={classes.draftBannerContent}>
                <Typography className={classes.draftBannerTitle}>
                  Unsaved draft found
                </Typography>
                <Typography className={classes.draftBannerText}>
                  {availableDraft.updatedAt
                    ? `A loan draft saved on ${new Date(
                        availableDraft.updatedAt
                      ).toLocaleString()} is available for your account.`
                    : "A previous loan draft is available for your account."}
                </Typography>
              </div>
              <div className={classes.draftBannerActions}>
                <A_Button
                  label="Start fresh"
                  premiumVariant="secondary"
                  onClick={handleDiscardDraft}
                  disabled={isSubmitting || isOptionsLoading}
                />
                <A_Button
                  label="Resume draft"
                  premiumVariant="primary"
                  onClick={handleRestoreDraft}
                  disabled={isSubmitting || isOptionsLoading}
                />
              </div>
            </div>
          ) : null}
          {activeStep === 0 &&
            Object.keys(searchResults).some((key) => {
              const results = searchResults[key] || [];
              if (!effectiveEditMode) return results.length > 0;
              return results.some(
                (res) => String(res["Loan Id"]) !== String(crmId)
              );
            }) && (
              <div className={classes.searchBanner}>
                <WarningIcon className={classes.warningIcon} />
                <div className={classes.draftBannerContent}>
                  <Typography className={classes.draftBannerTitle}>
                    Existing Applications Found
                  </Typography>
                  <Typography className={classes.draftBannerText}>
                    We found matching records for the postcode entered. Please
                    review the dropdown list in the Postcode field to avoid
                    duplicates.
                  </Typography>
                </div>
                <div
                  className={classes.draftBannerActions}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <A_Button
                    label="View List"
                    premiumVariant="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      focusField("Postcode");
                      toggleResultsVisibility("Postcode", true);
                    }}
                  />
                </div>
              </div>
            )}
          {/* <Typography variant="h6" className={classes.stepTitle}>
              {currentStep.title}
            </Typography> */}

          {isOptionsLoading ? (
            <div className={classes.loaderWrap}>
              <CircularProgress size={28} />
              <Typography className={classes.loaderText}>
                Loading data...
              </Typography>
            </div>
          ) : currentStep.title === "Fees" ? (
            renderFeesStep()
          ) : (
            renderStandardStep()
          )}
        </div>

        <div className={classes.actions}>
          <A_Button
            label="Back"
            premiumVariant="secondary"
            onClick={handleBack}
            disabled={activeStep === 0 || isSubmitting || isOptionsLoading}
            className={classes.actionButton}
          />

          <div className={classes.actionGroup}>
            {canUseDrafts ? (
              <A_Button
                label={
                  isDraftSaving ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    "Save Draft"
                  )
                }
                premiumVariant="secondary"
                onClick={handleSaveDraft}
                disabled={
                  isSubmitting ||
                  isOptionsLoading ||
                  isDraftSaving ||
                  !isDraftBootstrapComplete
                }
                className={classes.actionButton}
              />
            ) : null}
            {isDeveloperAutofillEnabled ? (
              <A_Button
                label="Random Fill"
                premiumVariant="secondary"
                onClick={handleDeveloperRandomFill}
                disabled={isSubmitting || isOptionsLoading}
                className={classes.actionButton}
              />
            ) : null}
            {!isLastStep ? (
              <A_Button
                label="Next"
                premiumVariant="primary"
                onClick={handleNext}
                disabled={isSubmitting || isOptionsLoading}
                className={classes.actionButton}
              />
            ) : null}
            {isLastStep ? (
              <A_Button
                label={effectiveEditMode ? "Update Loan" : "Submit Loan"}
                premiumVariant="primary"
                onClick={handleFinish}
                disabled={isSubmitting || isOptionsLoading}
                className={classes.actionButton}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : null
                }
              />
            ) : null}
          </div>
        </div>
      </Paper>
      {showFinishConfirmation && (
        <A_SimpleDialog
          open={showFinishConfirmation}
          title={
            effectiveEditMode
              ? "Confirm Loan Update"
              : "Confirm Loan Submission"
          }
          onClose={() => setShowFinishConfirmation(false)}
          dialogContent={
            effectiveEditMode
              ? "Are you sure you want to save the changes to this loan?"
              : "Are you sure you want to finish and save this loan?"
          }
          maxWidth="xs"
          dialogActions={
            <div className={classes.actionGroup}>
              <A_Button
                label="Cancel"
                premiumVariant="secondary"
                onClick={() => setShowFinishConfirmation(false)}
                disabled={isSubmitting}
              />
              <A_Button
                label={
                  isSubmitting
                    ? effectiveEditMode
                      ? "Updating..."
                      : "Submitting..."
                    : "Confirm"
                }
                premiumVariant="primary"
                onClick={executeSubmission}
                disabled={isSubmitting}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : null
                }
              />
            </div>
          }
        />
      )}
    </div>
  );
};

export const Add_Loan = React.memo(AddLoanComponent);

AddLoanComponent.propTypes = addLoanPropTypes;
AddLoanComponent.defaultProps = addLoanDefaultProps;

Add_Loan.displayName = "Add_Loan";

Add_Loan.propTypes = addLoanPropTypes;

Add_Loan.defaultProps = addLoanDefaultProps;

const mapStateToProps = (state) => ({
  currentPage: state.currentPage,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    Object.assign({}, toastActions, currentPageActions),
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps, null)(Add_Loan);
